import { ApiError } from '../../api';

/**
 * Maps a submit-run failure to copy the player will actually understand.
 * The raw ApiError message is dev-friendly (`/api/runs failed: 400 ...`)
 * which leaks URLs at the user, so we translate the most common reasons
 * and fall back to a generic line for anything else. Full error always
 * goes to the console for debugging.
 *
 * Mode-agnostic — both Pulse and Circles funnel through the same /api/runs
 * endpoint with the same error codes.
 */
export function friendlySubmitError(err: unknown): string {
  if (err instanceof TypeError) {
    return 'Network error — check your connection and try again.';
  }
  if (err instanceof ApiError) {
    if (err.status >= 500) {
      return 'Server error — please try again in a moment.';
    }
    if (err.code === 'replay_mismatch') {
      return "We couldn't verify this run. Try again, and recalibrate your audio offset if it keeps happening.";
    }
    if (err.code === 'anticheat_rejected') {
      return "We couldn't verify this run's timing. Reload the game and play again at normal speed.";
    }
    if (err.code === 'replay_required_for_completed_runs') {
      return 'Run not saved — replay data was missing.';
    }
    if (err.code === 'map_not_found') {
      return 'This map is no longer available.';
    }
    return err.code
      ? `Server rejected this run (${err.code}).`
      : `Server rejected this run (${err.status}).`;
  }
  if (err instanceof Error) {
    return err.message;
  }
  return 'Unknown error.';
}

/**
 * Maps engine death reasons to player-facing copy. Shared across every
 * mode so one renderer covers the whole `ReplayDeathReason` vocabulary:
 * `'drained'` (HP hit zero — Pulse / Circles / Guitar / Piano tutorial),
 * `'missed'` (Piano instadeath — one miss ends the run), `'collided'`
 * (Waves / Jump — touched an obstacle), and `'fell'` (Jump — dropped
 * into a pit).
 */
export function friendlyDeathReason(reason: string): string {
  switch (reason) {
    case 'drained': {
      return 'Drained — your HP hit zero.';
    }
    case 'missed': {
      return 'Missed a note — one slip ends the run.';
    }
    case 'collided': {
      return 'Crashed into an obstacle.';
    }
    case 'fell': {
      return 'Fell into a pit.';
    }
    default: {
      return `Run ended (${reason}).`;
    }
  }
}
